"use client";

import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";

import { cn } from "@/lib/utils";

type SliderProps = React.ComponentProps<typeof SliderPrimitive.Root> & {
  tooltip?: boolean;
  formatValue?: (value: number) => React.ReactNode;
};

function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  tooltip = false,
  formatValue,
  ...props
}: SliderProps) {
  const { onValueChange, orientation = "horizontal", ...restProps } = props;

  const _values = React.useMemo(
    () => (Array.isArray(value) ? value : Array.isArray(defaultValue) ? defaultValue : [min, max]),
    [value, defaultValue, min, max]
  );

  const [currentValues, setCurrentValues] = React.useState<number[]>(Array.isArray(_values) ? _values : []);

  React.useEffect(() => {
    setCurrentValues(Array.isArray(_values) ? _values : []);
  }, [_values]);

  const handleValueChange = React.useCallback(
    (vals: number[]) => {
      setCurrentValues(vals);
      onValueChange?.(vals);
    },
    [onValueChange]
  );

  return (
    <SliderPrimitive.Root
      data-slot='slider'
      defaultValue={defaultValue}
      value={value}
      min={min}
      max={max}
      onValueChange={handleValueChange}
      orientation={orientation}
      className={cn(
        "relative flex w-full touch-none items-center select-none data-[disabled]:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col",
        className
      )}
      {...restProps}
    >
      <SliderPrimitive.Track
        data-slot='slider-track'
        className={cn(
          "bg-muted relative grow overflow-hidden rounded-full data-[orientation=horizontal]:h-[2.5px] data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5"
        )}
      >
        <SliderPrimitive.Range
          data-slot='slider-range'
          className={cn(
            "bg-btn-primary absolute data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full"
          )}
        />
      </SliderPrimitive.Track>
      {Array.from({ length: _values.length }, (_, index) => (
        <SliderPrimitive.Thumb
          data-slot='slider-thumb'
          key={index}
          className='border-primary bg-background ring-ring/50 group relative block size-4 shrink-0 cursor-pointer rounded-full border shadow-sm transition-[color,box-shadow] hover:ring-4 focus-visible:ring-4 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50'
        >
          {tooltip && (
            <span
              className={cn(
                // base styles
                "bg-popover text-popover-foreground pointer-events-none absolute z-10 rounded px-2 py-1 text-xs opacity-0 shadow-md transition-opacity duration-150 group-hover:opacity-100",
                // position for horizontal vs vertical
                orientation === "vertical" ? "top-1/2 left-6 -translate-y-1/2" : "-top-8 left-1/2 -translate-x-1/2"
              )}
            >
              {typeof formatValue === "function" ? formatValue(currentValues[index] ?? 0) : (currentValues[index] ?? 0)}
            </span>
          )}
        </SliderPrimitive.Thumb>
      ))}
    </SliderPrimitive.Root>
  );
}

export { Slider };
